import { GTAG } from './constants';
import { IConsentCookieValue } from './types';

type GtagValue = (typeof GTAG.VALUE)[keyof typeof GTAG.VALUE];
type GtagAction = (typeof GTAG.ACTION)[keyof typeof GTAG.ACTION];

// Consent Mode v2 defines seven signal types. A `default` command must set ALL of
// them: any signal omitted from `consent default` is treated as granted by Google's
// tags, which would silently opt users in. Requiring every signal on this type makes
// that impossible to forget for any default we add.
interface IGtagConsentDefaults {
    analytics_storage: GtagValue;
    ad_storage: GtagValue;
    ad_user_data: GtagValue;
    ad_personalization: GtagValue;
    functionality_storage: GtagValue;
    personalization_storage: GtagValue;
    security_storage: GtagValue;
}

// The banner only collects an analytics decision, so an `update` carries just that
// signal. Every other signal stays at the value the `default` command set — Consent
// Mode leaves any signal absent from an update unchanged.
type IGtagConsentUpdate = Pick<IGtagConsentDefaults, 'analytics_storage'>;

// security_storage is strictly necessary (Google recommends always granting it),
// while functionality and personalization storage stay denied until a banner
// explicitly collects consent for them.
const PRIVACY_SAFE_DEFAULTS: IGtagConsentDefaults = {
    analytics_storage: GTAG.VALUE.DENIED,
    ad_storage: GTAG.VALUE.DENIED,
    ad_user_data: GTAG.VALUE.DENIED,
    ad_personalization: GTAG.VALUE.DENIED,
    functionality_storage: GTAG.VALUE.DENIED,
    personalization_storage: GTAG.VALUE.DENIED,
    security_storage: GTAG.VALUE.GRANTED,
};

function toGtagValue(granted: boolean): GtagValue {
    return granted ? GTAG.VALUE.GRANTED : GTAG.VALUE.DENIED;
}

function pushGtagConsent(action: GtagAction, signals: IGtagConsentDefaults | IGtagConsentUpdate): void {
    if (typeof window === 'undefined') return;
    const dataLayer = ((window as unknown as { [GTAG.DATA_LAYER_KEY]?: unknown[] })[GTAG.DATA_LAYER_KEY] ??= []);
    // gtag consent commands MUST be pushed as an `arguments` object, exactly as the
    // canonical `function gtag(){ dataLayer.push(arguments) }` stub does. Google's tag
    // silently ignores array-form pushes (e.g. `dataLayer.push(['consent','update',...])`),
    // so consent would never apply. Verified against /g/collect: array form stays
    // gcs=G100, arguments form flips to gcs=G111.
    function gtag(..._args: unknown[]) {
        dataLayer.push(arguments);
    }
    gtag(GTAG.COMMAND, action, signals);
}

/**
 * Sets the privacy-safe Consent Mode v2 defaults: every ad and analytics signal
 * denied, functionality and personalization storage denied, and only the
 * strictly-necessary security_storage granted. Must run before GTM loads so no
 * Google measurement tag fires in an unset consent state.
 */
export function pushConsentDefault(): void {
    pushGtagConsent(GTAG.ACTION.DEFAULT, PRIVACY_SAFE_DEFAULTS);
}

/**
 * Applies the user's saved analytics consent choice to Google Consent Mode v2. Ad
 * and supplementary signals are left untouched at their `default` state.
 *
 * KEEP IN SYNC with the non-shared JSP duplicates (same signal mapping and
 * granted/denied wire values):
 *   - ghix-web/src/main/webapp/resources/js/consent/consentCookie.js (`pushConsentUpdate`)
 *   - the inline pre-GTM replay snippet in
 *     ghix-web/src/main/webapp/WEB-INF/views/cookieConsent/cookieConsentHead.jsp
 */
export function pushConsentUpdate(consent: IConsentCookieValue): void {
    pushGtagConsent(GTAG.ACTION.UPDATE, {
        analytics_storage: toGtagValue(consent.analyticsConsentGranted),
    });
}
